import { mean } from 'lodash';

import { safeGetCanvasImageData } from 'lib/utils/canvas';

const LAPLACIAN_KERNEL = [0, 1, 0, 1, -4, 1, 0, 1, 0];

export interface LuminosityData {
  /**
   * Number between 0 and 1 representing the luminosity of an image.
   * A score of 0 means the image is completely black.
   * A score of 1 means the image is completely white.
   */
  luminosity: number;

  /**
   * Number between 0 and 1 representing the contrast of an image calculated using the RMS algorithm.
   *
   * A score of 0 means the image is completely uniform.
   * A score of 1 means the image has perfect constrast. Eg. an image with 50% black and 50% white pixels.
   *
   * In general this contrast is more consistent and not as susptible to small changes in the environment.
   * The benefit of this score is that it cannot be easily impacted by outliers in the setting but it is
   * also more difficult to use as it's more difficult to define what low contrast is.
   */
  rmsContrast: number;

  /**
   * Number between 0 and 1 representing the contrast of an image calculated using the difference
   * between the highest intensity pixels and the lowest intensity pixels.
   *
   * A score of 0 means the image is completely uniform.
   * A score of 1 means the image has perfect constrast. Eg. an image with 50% black and 50% white pixels.
   *
   * In general this contrast is very susptible to environmental change. Eg. a small bright spot in the
   * image can greatly impact the lowHighContrast. However one benefit of this score is that low contrast
   * readings tend to be fairly obvious and accurate.
   */
  lowHighContrast: number;
}

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Change contrast, pass value between -100 and 100.
 *
 * @deprecated
 */
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function contrastImage(imgData: ImageData, strength: number) {
  const d = imgData.data;
  strength = strength / 100 + 1; // convert to decimal & shift range: [0..2]
  const intercept = 128 * (1 - strength);

  for (let i = 0; i < d.length; i += 4) {
    // r, g, b
    d[i] = d[i] * strength + intercept;
    d[i + 1] = d[i + 1] * strength + intercept;
    d[i + 2] = d[i + 2] * strength + intercept;
  }
  return imgData;
}

/**
 * Change sharpness, pass value between 0 to 100.
 *
 * @deprecated
 */
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function sharpenImage(
  srcData: ImageData,
  dstData: ImageData,
  w: number,
  h: number,
  sharpness: number,
) {
  let x: number;
  let sx: string | number;
  let sy: string | number;
  let r: number;
  let g: number;
  let b: number;
  let dstOff: number;
  let srcOff: number;
  let wt: number;
  let cx: number;
  let cy: number;
  let scy: number;
  let scx: number;

  const weights = [0, -1, 0, -1, 5, -1, 0, -1, 0];
  const katet = Math.round(Math.sqrt(weights.length));
  // tslint:disable-next-line: no-bitwise
  const half = (katet * 0.5) | 0;
  const dstBuff = dstData.data;
  const srcBuff = srcData.data;
  let y = h;

  sharpness = sharpness / 100;

  while (y--) {
    x = w;
    while (x--) {
      sy = y;
      sx = x;
      dstOff = (y * w + x) * 4;
      r = 0;
      g = 0;
      b = 0;

      for (cy = 0; cy < katet; cy++) {
        for (cx = 0; cx < katet; cx++) {
          scy = sy + cy - half;
          scx = sx + cx - half;

          if (scy >= 0 && scy < h && scx >= 0 && scx < w) {
            srcOff = (scy * w + scx) * 4;
            wt = weights[cy * katet + cx];

            r += srcBuff[srcOff] * wt;
            g += srcBuff[srcOff + 1] * wt;
            b += srcBuff[srcOff + 2] * wt;
          }
        }
      }

      dstBuff[dstOff] = r * sharpness + srcBuff[dstOff] * (1 - sharpness);
      dstBuff[dstOff + 1] = g * sharpness + srcBuff[dstOff + 1] * (1 - sharpness);
      dstBuff[dstOff + 2] = b * sharpness + srcBuff[dstOff + 2] * (1 - sharpness);
      dstBuff[dstOff + 3] = srcBuff[dstOff + 3];
    }
  }
}

let captureCount = 0;
/**
 * Simply returns a larger number every time it is called. Effectively, always keeps the most recent images.
 *
 * TODO: This is temporary and hackily uses a global variable. Rework in future based on efficacy of change.
 */
export function noopClarityFilter(_canvasBuffer: HTMLCanvasElement): number {
  return captureCount++;
}

/**
 * TODO: Consider supporting a region argument like calculateLuminosity if this ends up being a performance drain.
 */
export function laplacianFilter(canvasBuffer: HTMLCanvasElement): number {
  // subsample only 75x75 pixels
  const skipSize = Math.max(Math.max(canvasBuffer.width, canvasBuffer.height) / 75, 1);

  // assigning getImageData to a variable first causes it to not be gc'd by safari it seems,
  // so directly reference .data
  const pixelData: Uint8ClampedArray | undefined = safeGetCanvasImageData(canvasBuffer)?.data;

  // treat as low clarity
  if (!pixelData) return 0;

  const pixelOut = new Uint8ClampedArray(5625);
  let j = 0;

  // grayscale the image
  for (let i = 0; i < pixelData.length; i += 4 * Math.floor(skipSize)) {
    const r = pixelData[i];
    const g = pixelData[i + 1];
    const b = pixelData[i + 2];
    // Avg is a number between 0 and 255
    // We compute true luminosity vs percieved luminosity because we are feeding
    // this image to computer algorithms, not for a human to review.
    const v = (r + g + b) / 3;
    pixelOut[j] = v;
    j++;
  }

  // apply laplacian filter onto image
  const laplaceFilter = convolve(pixelOut, LAPLACIAN_KERNEL, 75, 75);
  const laplaceVariance = variance(laplaceFilter);

  return laplaceVariance;
}

/**
 * Convolve a filter onto an image source
 *
 * @param pixelData canvas imageData.data
 * @param kernel kernal to apply. This assumes a kernel of size 3
 * @param width width of canvas
 * @param height height of canvas
 */
function convolve(
  pixelData: Uint8ClampedArray,
  kernel: Array<number>,
  width: number,
  height: number,
): Uint8ClampedArray {
  const side = Math.round(Math.sqrt(kernel.length));
  const halfSide = Math.floor(side / 2);

  const output = new Uint8ClampedArray(5625);

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const dstOff = y * width + x;
      let r = 0;
      for (let cy = 0; cy < side; cy++) {
        for (let cx = 0; cx < side; cx++) {
          const scy = Math.min(height - 1, Math.max(0, y + cy - halfSide));
          const scx = Math.min(width - 1, Math.max(0, x + cx - halfSide));
          const srcOff = scy * width + scx;
          const wt = kernel[cy * side + cx];
          r += pixelData[srcOff] * wt;
        }
      }
      output[dstOff] = r;
    }
  }
  return output;
}

/**
 * Mathematical function to calculate variance given a sequence of numbers
 *
 * @param arr array of numbers to calculate variance on
 */
function variance(numbers: Uint8ClampedArray): number {
  const meanNumbers = mean(numbers);
  const sumOfSquares = numbers.reduce((v, n) => v + (n - meanNumbers) ** 2, 0);
  return sumOfSquares / numbers.length;
}

/**
 * Calculate contrast using the most standard method. This compares the
 * difference between the most intense pixels in the image with the least
 * intense pixels.
 *
 * This method essentially checks the image histogram and returns a contrast
 * based on the range of values in the histogram. By default it uses the
 * 1st and 99th percentile in this comparison to reduce the possibility of
 * outliers affecting the contrast score (eg. a single white pixel in an
 * otherwise entirely black image).
 * @param histogram Represents a histogram where 0 is minimum intensity and
 * 255 is the maximum intensity. It is an array of numbers of size 256. Each
 * element represents the number of pixels of that intensity. Eg.
 * histogram[127] = 50 would mean 50 pixels in the image had an intensity
 * score of 127.
 * @param totalPixels The total number of pixels in the image. This number
 * must equal the sum of the numbers in the histogram.
 * @param lowerPercentile The percentile used for the lower reading in the
 * contrast calculation. Default is 1.
 * @param higherPercentile The percentile used for the higher reading in the
 * contrast calculation. Default is 99.
 * @returns A value between 0 and 255 indicating the contrast score of the
 * image. A score of 0 indicates extremely poor contrast while a score of
 * 255 indicates perfect contrast.
 */
function calculateLowHighContrast(
  histogram: Array<number>,
  totalPixels: number,
  lowerPercentile = 1,
  higherPercentile = 99,
): number {
  const lowerIndex = Math.floor((lowerPercentile / 100) * totalPixels);
  const higherIndex = Math.floor((higherPercentile / 100) * totalPixels);

  let lowerValue;
  let higherValue;

  let valuesSeen = 0;
  for (let i = 0, len = histogram.length; i < len; i++) {
    valuesSeen += histogram[i];
    if (valuesSeen > lowerIndex) {
      lowerValue = i;
      break;
    }
  }

  const higherIndexInversed = totalPixels - higherIndex;
  valuesSeen = 0;
  for (let i = histogram.length - 1; i >= 0; i--) {
    valuesSeen += histogram[i];
    if (valuesSeen > higherIndexInversed) {
      higherValue = i;
      break;
    }
  }

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return higherValue - lowerValue;
}

/**
 * Calculates the luminosity of an image or a region of an image.
 *
 * Currently disabled on production for performance reasons:
 * https://admin.withpersona.com/admin/flipper/features/inquiry:enable-low-light-detection-web
 *
 * @param canvasBuffer Buffer containing the image.
 * @param region Optional. The region to analyze.
 * @returns The luminosity and contrast scores.
 */
export function calculateLuminosity(
  canvasBuffer: HTMLCanvasElement,
  region: Rect = { x: 0, y: 0, width: canvasBuffer.width, height: canvasBuffer.height },
): LuminosityData | null {
  const ctx = canvasBuffer.getContext('2d', { willReadFrequently: true });

  // getContext can return null in some circumstances (e.g. OOM)
  if (ctx == null) {
    return null;
  }

  let pixelData: Uint8ClampedArray;
  try {
    // assigning getImageData to a variable first causes it to not be gc'd by safari it seems,
    // so directly reference .data
    pixelData = ctx.getImageData(region.x, region.y, region.width, region.height)?.data;

    if (!pixelData) {
      return null;
    }
  } catch (e) {
    if (
      // OOM
      e instanceof RangeError ||
      // Browser incompatibility?
      e instanceof DOMException ||
      // OOM
      (e instanceof Error && e.name === 'NS_ERROR_OUT_OF_MEMORY')
    ) {
      return null;
    } else {
      throw e;
    }
  }

  const totalPixels = pixelData.length / 4;
  let pixelSum = 0;
  const histogram = new Array<number>(256).fill(0);

  for (let i = 0, len = pixelData.length; i < len; i += 4) {
    const r = pixelData[i];
    const g = pixelData[i + 1];
    const b = pixelData[i + 2];

    // Avg is a number between 0 and 255
    // We compute true luminosity instead of percieved luminosity because we are feeding this to OCR and not a human's eyes.
    const avg = (r + g + b) / 3;

    const avgInt = Math.floor(avg);
    histogram[avgInt]++;

    pixelSum += avg;
  }

  const averagePixel = pixelSum / totalPixels;

  let sdSum = 0;
  for (let i = 0, len = pixelData.length; i < len; i += 4) {
    const r = pixelData[i];
    const g = pixelData[i + 1];
    const b = pixelData[i + 2];

    const avg = (r + g + b) / 3;
    const normalizedPixelDelta = (avg - averagePixel) / 255;
    sdSum += normalizedPixelDelta ** 2;
  }

  const rmsContrast = Math.sqrt(sdSum / totalPixels);

  const lowHighContrast = calculateLowHighContrast(histogram, totalPixels);

  // Normalize values to be between 0 and 1
  return {
    luminosity: averagePixel / 255,
    rmsContrast: rmsContrast / 0.5,
    lowHighContrast: lowHighContrast / 255,
  };
}

// Minimum value considered to be glare, out of 255
const GLARE_THRESHOLD = 254;

export function calculateGlareRatio(
  canvasBuffer: HTMLCanvasElement,
  threshold: number = GLARE_THRESHOLD,
) {
  const pixelData: Uint8ClampedArray | undefined = safeGetCanvasImageData(canvasBuffer)?.data;

  if (pixelData == null) return 0;

  // treat as no glare
  let numWhite = 0;

  const rows = canvasBuffer.height;
  const cols = canvasBuffer.width;

  // Sample pixels for performance, only want to go over 150x150 max
  const skipSize = Math.max(Math.max(rows, cols) / 150, 1);

  for (let y = 0; y < rows; y += skipSize) {
    for (let x = 0; x < cols; x += skipSize) {
      const i = (y * cols + x) * 4;

      const dx = 1 - (Math.abs(x - cols / 2) / cols) * 2;
      const dy = 1 - (Math.abs(y - rows / 2) / rows) * 2;
      const weight = dx * dy;

      if (
        pixelData[i] >= threshold &&
        pixelData[i + 1] >= threshold &&
        pixelData[i + 2] >= threshold
      ) {
        numWhite += weight * weight;
      }
    }
  }

  return numWhite / ((rows / skipSize) * (cols / skipSize));
}
